import { latinize, toCamelCase } from "../../core/helpers/string-util.js";
import AccountGroup from "./AccountGroup.js";
import AccountGroupAliasing from "./AccountGroupAliasing.js";
import accountGroupFactory from "./accountGroupFactory.js";
import AccountListGroup from "./AccountListGroup.js";
import IntervalAccountGroup from "./IntervalAccountGroup.js";
import Model from "../Model.js";
import polyglot from "../../core/polyglot/polyglot.js";

const chartOfAccountsGroupsOrder = ["System", "Agency", "Company"];

/**
 * A Chart of Accounts groups accounts in a tree.
 *
 * Account numbers are grouped in `intervalAccountGroup`s and
 * `accountListGroup`s, themselves grouped in (possibly nested)
 * `accountGroupComposite`s. These represent the chart of
 * account's main tree.
 *
 * A chart of accounts also has a secondary tree which contains
 * aliases and composites pointing towards the main tree.
 *
 * Some account groups are well known: those are called "known
 * groups". A known group is an account group that is used by kpis
 * or charts provided by the application. The server-side code has
 * a list of all known-group identifiers. A known group is thus an
 * account group whose identifier is in this list. The server
 * attaches a boolean to each account group to distinguish known
 * groups from others.
 *
 * @param {string} spec.name - Name of the chart of accounts
 * @param {string} spec.id - Id of the chart of accounts
 * @param {string} spec.language - Language to use in the UI for
 * accounting data content eg. for headings in reports.  It can be
 * different from the user language used for the whole application
 * @param {boolean} spec.isUserDefined - True if the chart of
 * accounts was created by hand, as opposed to standard chart of
 * accounts
 * @param {boolean} spec.isAccountMappingEnabled - True if the chart of
 * accounts can be used in the account mapping editor
 * @param {boolean} spec.validateGaps - True if interval
 * gaps are forbidden in the chart of accounts
 * @param {string} spec.ownerId - Identifier of the entity owning
 * the chart of accounts
 * @param {accountGroupComposite} [spec.mainTree] - The main tree
 * contains all accounts (in intervals or lists) of the chart of
 * accounts. All accounts are organized in a tree composed of
 * `accountGroupComposite`s.
 * @param {accountGroupComposite} [spec.secondaryTree] - The
 * secondary tree contains `accountGroupAliasing` and
 * `accountGroupComposite` nodes pointing to nodes in the main
 * tree.
 */
export default class ChartOfAccounts extends Model {
	constructor(spec = {}) {
		super(...arguments);

		this._name = spec.name;
		this._id = spec.id;
		this._language = spec.language || "";
		this._isUserDefined = spec.isUserDefined || false;
		this._isAccountMappingEnabled = spec.isAccountMappingEnabled || false;
		this._validateGaps = spec.validateGaps || false;
		this._ownerId = spec.ownerId;
		this._ownerType = spec.ownerType;
		this._mainTree = spec.mainTree || new AccountGroup();
		this._secondaryTree = spec.secondaryTree || new AccountGroup();

		this._mainTree.setChartOfAccounts(this);
		this._secondaryTree.setChartOfAccounts(this);
	}

	isChildOf({ childAccountGroupId, parentAccountGroupId }) {
		const parentAccountGroup = this.findById(parentAccountGroupId);

		return parentAccountGroup?.findById(childAccountGroupId);
	}

	hasAccountInGroup({ account, accountGroupId }) {
		return this.findById(accountGroupId)?.hasAccount(account);
	}

	getName() {
		return this._name;
	}

	setName(newName) {
		this._name = newName;
	}

	getId() {
		return this._id;
	}

	getChartOfAccounts() {
		return this;
	}

	getLanguage() {
		return this._language;
	}

	setLanguage(newLanguage) {
		this._language = newLanguage;
	}

	getType() {
		return "ChartOfAccounts";
	}

	isUserDefined() {
		return this._isUserDefined;
	}

	isAccountMappingEnabled() {
		return this._isAccountMappingEnabled;
	}

	validatesGaps() {
		return this._validateGaps;
	}

	setGapsValidation(newValue) {
		this._validateGaps = newValue;
	}

	getMainTree() {
		return this._mainTree;
	}

	getSecondaryTree() {
		return this._secondaryTree;
	}

	getAllGroups() {
		return this._mainTree.getAllGroups();
	}

	/**
	 * Returns all accounts from `fiscalYear` that are not contained
	 * by any group in the CoA tree.
	 */
	getUnmappedAccounts(fiscalYear) {
		return fiscalYear
			.getAccounts()
			.filter((account) => !this._mainTree.hasAccount(account));
	}

	createGroup(options) {
		return this._createChild(options, AccountGroup);
	}

	createInterval(options) {
		return this._createChild(options, IntervalAccountGroup);
	}

	createAccountList(options) {
		return this._createChild(options, AccountListGroup);
	}

	createAliasingGroup(options) {
		return this._createChild(options, AccountGroupAliasing);
	}

	includes(group) {
		return this._mainTree.includes(group);
	}

	findById(id) {
		// We do a shallow search in the secondary tree as all
		// nodes below the top-level will also be in the main
		// tree. We then avoid searching many nodes that are
		// referenced from the secondary tree twice.
		return (
			this._secondaryTree
				.getChildren()
				.find((child) => child.getId() === id) ||
			this._mainTree.findById(id)
		);
	}

	getAccountName(id) {
		return this.findById(id)?.getName();
	}

	getOwnerType() {
		return this._ownerType;
	}

	setOwnerType(ownerType) {
		this._ownerType = ownerType;
	}

	getOwnerId() {
		return this._ownerId;
	}

	setOwnerId(ownerId) {
		this._ownerId = ownerId;
	}

	withLanguage(callback) {
		const language = this._language || polyglot.getLanguage();

		polyglot.withLanguage(language, callback);
	}

	hasAliasingKnownGroup(group) {
		return this._secondaryTree
			.getChildren()
			.some(
				(child) => child.isKnownGroup() && child.isAliasingGroup(group),
			);
	}

	hasAliasingGroup(group) {
		return this._secondaryTree
			.getChildren()
			.some((child) => child.isAliasingGroup(group));
	}

	hasAccounts() {
		return this._mainTree.hasAccounts();
	}

	hasAccount(account) {
		return this._mainTree.hasAccount(account);
	}

	isEditable() {
		// For now, an editable coa is a coa which only contains
		// aliases in the secondary tree. Later, we should allow
		// all coas, in particular, those that contain account
		// group composites as direct children of the secondary
		// tree.
		return !this._secondaryTree
			.getChildren()
			.some((child) => child.getType() !== "AccountGroupAliasing");
	}

	belongsTo(owner) {
		return this._ownerId === owner.getLegacyId();
	}

	setOwnership(owner) {
		this.setOwnerType(owner.getType());
		this.setOwnerId(owner.getLegacyId());
	}

	compare(chartOfAccounts) {
		const groupsComparison =
			this._getGroupPosition(this) -
			this._getGroupPosition(chartOfAccounts);

		return groupsComparison || this.compareNames(chartOfAccounts);
	}

	compareNames(chartOfAccounts) {
		return this.getName().localeCompare(chartOfAccounts.getName());
	}

	_getGroupPosition(chartOfAccounts) {
		const type = chartOfAccounts.isUserDefined()
			? chartOfAccounts.getOwnerType()
			: "System";

		return chartOfAccountsGroupsOrder.indexOf(type);
	}

	_generateId(name) {
		name = latinize(name);
		const id = toCamelCase(name);
		const count = this._matchingIds(id);
		if (count === 0) {
			return id;
		}
		return id + count;
	}

	_matchingIds(id) {
		const regex = new RegExp(`^${id}[0-9]*$`);
		return this._mainTree.count(
			(child) => child.getId() && !!child.getId().match(regex),
		);
	}

	_createChild(options, Class) {
		const parent = options.parent;
		options.id ||= this._generateId(options.name);

		const child = new Class(options);
		child.setChartOfAccounts(this);

		parent.addChild(child);

		return child;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.name = this._name;
		properties.id = this._id;
		properties.language = this._language;
		properties.isUserDefined = this._isUserDefined;
		properties.isAccountMappingEnabled = this._isAccountMappingEnabled;
		properties.validateGaps = this._validateGaps;
		properties.mainTree = this._mainTree;
		properties.secondaryTree = this._secondaryTree;
		properties.ownerId = this._ownerId;
		properties.ownerType = this._ownerType;

		return properties;
	}

	static getType() {
		return "ChartOfAccounts";
	}

	static getDefaultOwnerType() {
		return "Company";
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.id ||= data.chartOfAccountsId;

		data.mainTree &&= accountGroupFactory.materialize(
			data.mainTree,
			context,
		);

		data.secondaryTree &&= accountGroupFactory.materialize(
			data.secondaryTree,
			{ mainTree: data.mainTree },
		);

		data.ownerId ||= data.ownerCompanyId;
	}
}
